import { useRef } from 'react';
import { useMutation } from '@apollo/client';
import { assignInternalDentistIdTemplate } from 'apollo/templates/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { DentistLocalIds } from 'models/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

/**
 * Gives a dentist the lab's next internal ID at the moment they are attached to a lab
 * order, so the number exists before the order is saved. The mutation is only sent when
 * the lab has enabled AUTO_INCREMENT_LOCAL_DENTIST_ID, and the backend is a no-op for a
 * dentist that already has an ID, so calling this on every selection is safe.
 *
 * A lab whose counter has not been seeded gets a dialog telling them the setup is
 * incomplete. Every other failure is only logged, because the lab must still be able to
 * place the order.
 */
export const useAssignInternalDentistId = () => {
    const lab = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const isEnabled = Boolean(
        customerSettings.getCustomerSetting(
            lab?.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.AUTO_INCREMENT_LOCAL_DENTIST_ID,
        ),
    );
    const [assignInternalDentistId] = useMutation(assignInternalDentistIdTemplate);
    const hasReportedSetupIssue = useRef(false);

    const assignIdToDentist = async (dentistId?: string, clinicId?: string): Promise<DentistLocalIds[] | undefined> => {
        if (!isEnabled || !dentistId || !clinicId) {
            return undefined;
        }
        try {
            const { data } = await assignInternalDentistId({
                variables: { dentistId, clinicId },
            });
            return data?.assignInternalDentistId?.dentistLocalIds;
        } catch (error: any) {
            // A missing counter is a setup problem the lab has to see, so it goes to the
            // UserFrontendFeedback dialog. Anything else is only logged — sending every
            // failure to showBoundary would drop the lab onto the full-page error screen
            // in the middle of an order.
            if (error?.graphQLErrors?.[0]?.extensions?.exception?.type === 'USER_FRONTEND_FEEDBACK') {
                // Raised once only. The counter is missing for the whole lab, so every
                // dentist picked while building this order would otherwise repeat the dialog.
                if (!hasReportedSetupIssue.current) {
                    hasReportedSetupIssue.current = true;
                    showBoundary(error);
                }
                return undefined;
            }
            console.error('Failed to assign internal dentist ID', error);
            return undefined;
        }
    };

    return { assignIdToDentist, isInternalDentistIdAutoAssigned: isEnabled };
};
